import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { commonSelectMessages } from '../../messages';
import { DocumentType, TabItem } from '../../model';
import { ChipConfig } from '../../model/chip-config';
import {
  DocumentsFilter,
  DocumentsFilterMap,
  documentsListActions,
  DocumentsOrderCriteria,
  getDocumentsListFilter,
  getDocumentsTypeFilters,
  labelForDocumentsChip,
} from '../../store/features/documents';
import { toggleShowDocumentsType } from '../../store/features/documents/documents-list/documents-list-thunks';
import { useAppDispatch, useAppSelector } from '../redux';

export interface DocumentsFilterLogic {
  selectedOrderCriteria: DocumentsOrderCriteria;
  chipsConfig: ChipConfig<DocumentType>[];
  documentsOrderCriteria: TabItem<DocumentsOrderCriteria>[];
  showUnreadDocuments: boolean;
  handleChipClick: (documentType: DocumentType) => void;
  handleOrderCriteriaSelection: (orderCriteria: DocumentsOrderCriteria) => void;
  handleShowUnreadDocumentsChange: (value: boolean) => void;
}

export const useDocumentsFilterLogic = (): DocumentsFilterLogic => {
  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();
  const documentsTypeFilters: DocumentsFilterMap = useAppSelector(
    getDocumentsTypeFilters
  );
  const { orderCriteria, showUnreadDocuments }: DocumentsFilter =
    useAppSelector(getDocumentsListFilter);

  const handleChipClick = useCallback(
    (documentType: DocumentType) => {
      dispatch(toggleShowDocumentsType(documentType));
    },
    [dispatch]
  );

  const handleOrderCriteriaSelection = useCallback(
    (criteria: DocumentsOrderCriteria) => {
      dispatch(documentsListActions.setOrderCriteria(criteria));
    },
    [dispatch]
  );

  const handleShowUnreadDocumentsChange = useCallback(
    (value: boolean) => {
      dispatch(documentsListActions.setShowUnreadDocuments(value));
    },
    [dispatch]
  );

  const chipsConfig = useMemo(() => {
    return Object.keys(documentsTypeFilters).map((key: DocumentType) => {
      const values = documentsTypeFilters[key];
      return {
        badgeCount: values.unreadCount,
        isSelected: values.show,
        label: labelForDocumentsChip(key),
        value: key,
      };
    });
  }, [documentsTypeFilters]);

  const documentsOrderCriteria: TabItem<DocumentsOrderCriteria>[] = useMemo<
    TabItem<DocumentsOrderCriteria>[]
  >(
    () => [
      {
        type: 'UploadDate',
        label: formatMessage(commonSelectMessages.uploadDate),
      },
      {
        type: 'Title',
        label: formatMessage(commonSelectMessages.title),
      },
    ],
    [formatMessage]
  );

  return {
    selectedOrderCriteria: orderCriteria,
    documentsOrderCriteria,
    chipsConfig,
    showUnreadDocuments,
    handleChipClick,
    handleOrderCriteriaSelection,
    handleShowUnreadDocumentsChange,
  };
};
